import React, { ReactElement } from 'react';
import {
  Falsy,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputChangeEventData,
  TextInputContentSizeChangeEventData,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import Colors from '../theming/Colors';
import Spacing from '../theming/Spacing';
import { colorForScheme } from '../theming/Theming';
import KitText from './KitText';

// Helper function to extract margin style from margin props
const getMarginStyle = (props: IKitInputProps) => {
  const style: any = {};

  if (props.margin !== undefined) style.margin = props.margin;

  if (props.marginTop !== undefined) style.marginTop = props.marginTop;

  if (props.marginBottom !== undefined) style.marginBottom = props.marginBottom;

  if (props.marginLeft !== undefined) style.marginLeft = props.marginLeft;

  if (props.marginRight !== undefined) style.marginRight = props.marginRight;

  if (props.marginHorizontal !== undefined) {
    style.marginHorizontal = props.marginHorizontal;
  }

  if (props.marginVertical !== undefined) {
    style.marginVertical = props.marginVertical;
  }

  return style;
};

//******************************************************************************
// Types
//******************************************************************************
export interface IKitInputProps {
  allowFontScaling?: boolean;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  autoComplete?: TextInputProps['autoComplete'];
  autoCorrect?: boolean;
  autoFocus?: boolean;
  blurOnSubmit?: boolean;
  onContentSizeChange?: (
    e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>
  ) => void;
  defaultValue?: string;
  editable?: boolean;
  inputBorder?: string;
  inputRef?: any;
  inputStyle?: StyleProp<TextStyle>;
  isValid?: boolean;
  keyboardType?:
    | 'decimal-pad'
    | 'default'
    | 'email-address'
    | 'number-pad'
    | 'numeric'
    | 'phone-pad';
  id?: string;
  key?: string | number;
  label?: string;
  labelStyle?: TextStyle;
  leftIcon?: ReactElement;
  leftIconStyle?: StyleProp<ViewStyle>;
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  marginHorizontal?: number;
  marginVertical?: number;
  multiline?: boolean;
  onChangeText?: (inputText: string) => void;
  onLayout?: (event: any) => void;
  onFocus?: (...args: unknown[]) => void;
  onBlur?: () => void;
  onPressIn?: () => void;
  onSubmitEditing?: (...args: unknown[]) => void;
  outline?: boolean;
  placeholder?: string;
  placeholderTextColor?: string;
  returnKeyType?: 'done' | 'go' | 'next' | 'search' | 'send';
  rightIcon?: Falsy | ReactElement;
  rightIconStyle?: ViewStyle;
  rightLabel?: string;
  rightLabelStyle?: TextStyle;
  secureTextEntry?: boolean;
  selectTextOnFocus?: boolean;
  selectionColor?: string;
  showSoftInputOnFocus?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  value?: string;
  maxLength?: number;
}

export interface IState {
  focused: boolean;
  inputHeight: number;
}

const MULTILINE_MAX_HEIGHT = 200;

//******************************************************************************
// Component
//******************************************************************************

class KitInput extends React.Component<IKitInputProps, IState> {
  static defaultProps: Partial<IKitInputProps> = {
    autoCapitalize: 'sentences',
    autoCorrect: true,
    autoFocus: false,
    blurOnSubmit: undefined,
    editable: true,
    inputBorder: 'transparent',
    inputStyle: {},
    isValid: true,
    keyboardType: 'default',
    multiline: false,
    outline: false,
    placeholder: '',
    returnKeyType: undefined,
    rightLabel: undefined,
    rightLabelStyle: {},
    secureTextEntry: false,
    selectTextOnFocus: false,
    style: {},
    testID: '',
    maxLength: undefined,
  };

  //****************************************************************************
  // State
  //****************************************************************************

  state: IState = {
    focused: false,
    inputHeight: 100,
  };

  //****************************************************************************
  // Methods
  //****************************************************************************

  private _onInputFocus = () => {
    this.setState({ focused: true });
    this.props.onFocus?.();
  };

  private _onInputBlur = () => {
    this.setState({ focused: false });
    this.props.onBlur?.();
  };

  private _onContentSizeChange = ({
    nativeEvent,
  }: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
    if (Platform.OS !== 'web') return;

    const { contentSize } = nativeEvent;
    const newHeight = contentSize.height + 12;
    this.setState({
      inputHeight: Math.max(
        100,
        newHeight > MULTILINE_MAX_HEIGHT ? MULTILINE_MAX_HEIGHT : newHeight
      ),
    });
  };

  //****************************************************************************
  // Lifecycle
  //****************************************************************************

  render(): ReactElement {
    const {
      allowFontScaling,
      autoCapitalize,
      autoComplete,
      autoCorrect,
      autoFocus,
      blurOnSubmit,
      onContentSizeChange,
      defaultValue,
      editable,
      inputBorder,
      inputRef,
      inputStyle,
      isValid,
      keyboardType,
      label,
      labelStyle,
      leftIcon,
      leftIconStyle,
      margin,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      marginHorizontal,
      marginVertical,
      multiline,
      onChangeText,
      onLayout,
      onSubmitEditing,
      outline,
      placeholder,
      placeholderTextColor,
      returnKeyType,
      rightIcon,
      rightIconStyle,
      rightLabel,
      rightLabelStyle,
      secureTextEntry,
      selectTextOnFocus,
      selectionColor,
      showSoftInputOnFocus = true,
      style,
      testID,
      value,
      onPressIn,
      maxLength,
      id,
      key,
    } = this.props;

    return (
      <View
        style={[
          { opacity: editable ? 1.0 : !editable && outline ? 1.0 : 0.5 },
          getMarginStyle(this.props),
          style,
        ]}
      >
        {label ? (
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <KitText
              color={Colors.N500}
              fontSize={14}
              semiBold
              style={{ lineHeight: 18, marginBottom: Spacing.s, ...labelStyle }}
              testID={`Label ${label}`}
            >
              {label}
            </KitText>
            {rightLabel?.length ? (
              <KitText
                color={Colors.N500}
                fontSize={14}
                style={{
                  lineHeight: 18,
                  marginBottom: Spacing.m,
                  ...rightLabelStyle,
                }}
              >
                {rightLabel}
              </KitText>
            ) : null}
          </View>
        ) : null}
        <TextInput
          testID={testID || placeholder}
          showSoftInputOnFocus={showSoftInputOnFocus}
          allowFontScaling={allowFontScaling}
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          autoFocus={autoFocus}
          onPressIn={onPressIn}
          blurOnSubmit={blurOnSubmit}
          defaultValue={defaultValue}
          editable={editable}
          keyboardType={keyboardType}
          multiline={multiline}
          onBlur={this._onInputBlur}
          maxLength={maxLength}
          id={id}
          key={key}
          onChange={(
            event: NativeSyntheticEvent<
              TextInputChangeEventData & {
                srcElement?: { scrollHeight: number };
              }
            >
          ) => {
            if (Platform.OS === 'web') {
              if (!multiline) {
                this.setState({
                  inputHeight: event.nativeEvent.srcElement
                    ?.scrollHeight as number,
                });
              }
            }
          }}
          onChangeText={onChangeText}
          onLayout={onLayout}
          onContentSizeChange={
            onContentSizeChange
              ? onContentSizeChange
              : this._onContentSizeChange
          }
          onFocus={this._onInputFocus}
          onSubmitEditing={onSubmitEditing}
          placeholder={placeholder}
          placeholderTextColor={colorForScheme({
            default: placeholderTextColor || Colors.N500,
          })}
          ref={inputRef}
          returnKeyType={returnKeyType}
          secureTextEntry={secureTextEntry}
          selectTextOnFocus={selectTextOnFocus}
          selectionColor={selectionColor}
          style={[
            {
              backgroundColor: colorForScheme({
                default: outline ? Colors.N0 : Colors.N50,
              }),
              borderRadius: 8,
              borderWidth: 2,
              color: colorForScheme({ default: Colors.N900 }),
              fontSize: 17,
              height: 44,
              lineHeight: 20,
              ...(Platform.OS === 'web' ? { fontFamily: 'inherit' } : {}),
              paddingBottom: 0,
              paddingLeft: 14,
              paddingRight: 14,
              paddingTop: 0,
            },
            !isValid
              ? { borderColor: colorForScheme({ default: Colors.R500 }) }
              : this.state.focused
              ? { borderColor: colorForScheme({ default: Colors.N900 }) }
              : outline
              ? { borderColor: colorForScheme({ default: Colors.N100 }) }
              : { borderColor: inputBorder },
            leftIcon ? { paddingLeft: 42 } : {},
            Platform.OS === 'web' ? ({ outlineWidth: 0 } as any) : {},
            multiline
              ? {
                  height: this.state.inputHeight,
                  maxHeight: MULTILINE_MAX_HEIGHT,
                  minHeight: 100,
                  paddingBottom: 12,
                  paddingTop: 12,
                  textAlignVertical: 'top',
                }
              : {},
            { width: '100%' },
            inputStyle,
          ]}
          value={value}
        />
        {leftIcon ? (
          <View style={[styles.leftIcon, leftIconStyle]}>{leftIcon}</View>
        ) : null}
        {rightIcon ? (
          <View
            style={{
              bottom: 12,
              height: 20,
              position: 'absolute',
              right: 12,
              width: 20,
              ...rightIconStyle,
            }}
          >
            {rightIcon}
          </View>
        ) : null}
      </View>
    );
  }
}

export default KitInput;

const styles = StyleSheet.create({
  leftIcon: {
    bottom: 0,
    height: '100%',
    justifyContent: 'center',
    left: 14,
    position: 'absolute',
    top: 0,
  },
});
